import { FunctionComponent, useDeferredValue, useEffect, useMemo, useRef, useState } from "react"
import {
  Box,
  ClickAwayListener,
  IconButton,
  Popper,
  TextField,
  Theme,
  Tooltip,
  Typography,
} from "@mui/material"
import { GraphNodeData, GraphNodeEdgeData, GroupNodeData } from "@store/services/converter/types"
import { SxProps, useTheme } from "@mui/material/styles"
import List from "@mui/material/List"
import { ArrowDropDownIcon, ClearIcon } from "@mui/x-date-pickers"
import { GroupNodeLabel } from "@features/graph/graphViewModel"
import logger from "@utils/logger"
import { isLagNodeWithCorrespondingNode } from "@utils/node"

export interface GroupAutocompleteDisabledFilter {
  label: string
  reason: string
}

interface GroupAutocompleteProps {
  graphData: GraphNodeEdgeData
  value: GroupNodeLabel | null
  label?: string
  groupFilter?: string[]
  optionsFilter?: GroupNodeLabel[]
  disabledFilter?: GroupAutocompleteDisabledFilter[]
  onChange: (newValue: GroupNodeLabel | null) => void
  sx?: SxProps<Theme>
  disableClearable?: boolean
}

interface GroupTreeNode {
  label: string
  children: GroupTreeNode[]
  nodes: GraphNodeData[]
  value?: GroupNodeData
}

interface AutocompleteListItemProps {
  node: GroupTreeNode
  disabledFilter?: GroupAutocompleteDisabledFilter[]
  onValueClick: (node: GraphNodeData, group?: GroupNodeData) => void
}

const NOT_GROUPED_NODES_LABEL = "---"

export const GroupAutocomplete: FunctionComponent<GroupAutocompleteProps> = ({
  graphData,
  value,
  label,
  groupFilter,
  optionsFilter,
  onChange,
  disabledFilter,
  disableClearable,
  sx,
}) => {
  const theme = useTheme()
  const [isOpen, setIsOpen] = useState<boolean>(false)
  const [isForceDropDown, setIsForceDropDown] = useState(false)
  const [anchorEl, setAnchorEl] = useState<HTMLDivElement>()
  const anchorRef = useRef<HTMLDivElement | null>(null)
  const inputRef = useRef<HTMLDivElement | null>(null)

  const [textValue, setTextValue] = useState<string>(value?.label || "")
  const labels = useMemo(
    () => createLabels(graphData, value?.label || "", groupFilter, optionsFilter),
    [graphData, groupFilter, optionsFilter, value?.label],
  )
  const defaultOptions = useDeferredValue<GroupTreeNode[]>(labels)
  const [filteredOptions, setFilteredOptions] = useState<GroupTreeNode[]>(defaultOptions)
  const deferredFilteredOptions = useDeferredValue(filteredOptions)

  useEffect(() => {
    setTextValue(value?.label || "")
  }, [value])

  useEffect(() => {
    if (!anchorRef?.current) return
    setAnchorEl(anchorRef.current)
  }, [anchorRef])

  const handleValueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setTextValue(e.target.value)
    setIsForceDropDown(false)
    setIsOpen(true)
    setFilteredOptions(createLabels(graphData, e.target.value, groupFilter, optionsFilter))
  }

  const handleValueClick = (node: GraphNodeData, group?: GroupNodeData) => {
    setTextValue(node.display.label)
    setIsOpen(false)
    setIsForceDropDown(false)
    onChange({
      type: node.type,
      label: node.display.label,
      format: node.display["Unit Format"] || "",
      key: node.node.name,
      group: group?.group.label || "",
      sort: "",
    })
  }

  const handleDropDownIconClick = () => {
    setIsOpen(!isOpen)
    setIsForceDropDown(!isOpen)
  }

  const handleClearIconClick = () => {
    setTextValue("")
    setIsOpen(true)
    inputRef.current?.focus()
  }

  const handleClickAway = () => {
    setIsOpen(false)
  }

  const defaultView = useDeferredValue(
    <List>
      {defaultOptions.map((node, idx) => (
        <AutocompleteListItem key={idx} onValueClick={handleValueClick} node={node} />
      ))}
    </List>,
  )

  return (
    <ClickAwayListener onClickAway={handleClickAway}>
      <Box style={{ width: "100%" }} sx={sx} ref={anchorRef}>
        <div style={{ width: "100%" }}>
          <TextField
            variant="outlined"
            inputRef={inputRef}
            placeholder={label}
            InputProps={{
              disableUnderline: true,
              endAdornment: (
                <div
                  style={{
                    position: "absolute",
                    borderColor: "grey",
                    backgroundColor: "#ffffff",
                    right: 5,
                    top: "50%",
                    transform: "translate(0, -50%)",
                  }}
                >
                  {!disableClearable && (
                    <IconButton
                      size="small"
                      onClick={handleClearIconClick}
                      style={{ color: "grey", visibility: textValue ? "visible" : "hidden" }}
                    >
                      <ClearIcon />
                    </IconButton>
                  )}
                  <IconButton
                    size="small"
                    onClick={handleDropDownIconClick}
                    style={{ color: "grey", cursor: "pointer" }}
                  >
                    <ArrowDropDownIcon />
                  </IconButton>
                </div>
              ),
            }}
            label={label}
            size="small"
            sx={{ width: "100%" }}
            value={textValue}
            onChange={handleValueChange}
            onFocus={(e) => {
              e.target.select()
              setIsOpen(true)
            }}
          />
        </div>
        <Popper
          open={isOpen}
          anchorEl={anchorEl}
          disablePortal={true}
          style={{
            zIndex: theme.zIndex.modal,
            minWidth: anchorEl?.clientWidth,
            background: "white" /*, width: anchorEl?.clientWidth - disable for now*/,
          }}
        >
          <div
            style={{
              overflow: "auto",
              maxHeight: 300,
              minHeight: 60,
              border: "1px solid #cccccc",
              borderRadius: 1,
            }}
          >
            {isForceDropDown ? (
              defaultView
            ) : deferredFilteredOptions.length === 0 ? (
              <div style={{ position: "absolute", top: "30%", textAlign: "center", width: "100%" }}>
                <b>No options</b>
              </div>
            ) : (
              deferredFilteredOptions.map((node, idx) => (
                <AutocompleteListItem
                  key={idx}
                  onValueClick={handleValueClick}
                  node={node}
                  disabledFilter={disabledFilter}
                />
              ))
            )}
          </div>
        </Popper>
      </Box>
    </ClickAwayListener>
  )
}

const AutocompleteListItem: FunctionComponent<AutocompleteListItemProps> = ({
  node,
  disabledFilter,
  onValueClick,
}) => {
  const renderedNodes = node.nodes.map((nodeItem) => {
    const disabledOption = disabledFilter?.find(({ label }) => label === nodeItem.display.label)

    return {
      nodeItem,
      disabledReason: disabledOption?.reason,
    }
  })

  return (
    <div style={{ padding: 10 }}>
      <label>{node.label}</label>
      {renderedNodes.map(({ nodeItem, disabledReason }, index) =>
        disabledReason ? (
          <Tooltip title={disabledReason} key={index}>
            <Typography sx={{ padding: 2, color: "#aaaaaa" }} fontWeight="bold">
              {nodeItem.display.label}
            </Typography>
          </Tooltip>
        ) : (
          <Typography
            sx={{ cursor: "pointer", padding: 2, "&:hover": { backgroundColor: "#eeeeee" } }}
            key={index}
            fontWeight="bold"
            onClick={() => onValueClick(nodeItem, node.value)}
          >
            {nodeItem.display.label}
          </Typography>
        ),
      )}
      {node.children.length > 0 &&
        node.children.map((node, idx) => (
          <AutocompleteListItem
            key={idx}
            onValueClick={onValueClick}
            node={node}
            disabledFilter={disabledFilter}
          />
        ))}
    </div>
  )
}

/*
 * Creates Map of sorted groups in order from the most outter groups to the most inner groups.
 */
const getSortedGroupMap = (groupMap: GraphNodeEdgeData["groupMap"]) => {
  const map = new Map<string, GroupNodeData>()

  Object.values(groupMap)
    .toSorted((a, b) => (a.zIndex > b.zIndex ? 1 : -1))
    .forEach((group) => map.set(group.id, group))

  return map
}

const createGroupTree = (groupMap: GraphNodeEdgeData["groupMap"]) => {
  const root: GroupTreeNode = { label: "root", children: [], nodes: [] }
  const sortedGroupMap = getSortedGroupMap(groupMap)
  const flattenTreeLevels = new Map<string, GroupTreeNode>()

  sortedGroupMap.forEach((groupData, groupId) => {
    const node: GroupTreeNode = {
      label: groupData.group.label,
      value: groupData,
      nodes: [],
      children: [],
    }

    const parentGroupId = groupData.group.parent

    if (parentGroupId) {
      const parentGroup = flattenTreeLevels.get(parentGroupId)
      if (!parentGroup) {
        logger.error(`Parent not found for ${groupData}`, "group", groupData)
        return
      }
      parentGroup.children.push(node)
    } else {
      root.children.push(node)
    }

    flattenTreeLevels.set(groupId, node)
  })

  // Nodes not placed in any group should be grouped at the end
  root.children.push({
    label: NOT_GROUPED_NODES_LABEL,
    nodes: [],
    children: [],
  })

  return root
}

const flattenGroupTree = (tree: GroupTreeNode) => {
  const levels: GroupTreeNode[] = [tree]

  tree.children.forEach((child) => levels.push(...flattenGroupTree(child)))

  return levels
}

const fillGroupTree = (
  tree: GroupTreeNode,
  groupMap: GraphNodeEdgeData["groupMap"],
  nodeMap: GraphNodeEdgeData["nodeMap"],
) => {
  const updatedTree = structuredClone(tree)

  const treeLevels = flattenGroupTree(updatedTree)
  const notGroupedLevel = treeLevels.find((level) => level.label === NOT_GROUPED_NODES_LABEL)

  Object.values(nodeMap).forEach((node) => {
    const groupsId = node.display.groups ?? []
    const theMostNestedGroupId = groupsId
      .map((id) => groupMap[id])
      .filter((group) => group !== undefined)
      .toSorted((groupA, groupB) => (groupA.zIndex > groupB.zIndex ? -1 : 1))[0]?.id

    if (!theMostNestedGroupId) {
      notGroupedLevel?.nodes.push(node)
    } else {
      const groupTreeLevel = treeLevels.find((level) => level.value?.id === theMostNestedGroupId)
      if (!groupTreeLevel) return

      groupTreeLevel.nodes.push(node)
    }
  })

  return updatedTree
}

const doesGroupTreeHasGroup = (tree: GroupTreeNode, groupId: string): boolean => {
  return tree.children.some((level) => {
    const isThatGroup = level.value?.id ?? "" === groupId
    if (isThatGroup) return true

    return doesGroupTreeHasGroup(level, groupId)
  })
}

const limitGroupTreeToGroups = (tree: GroupTreeNode, groupIds: string[]) => {
  if (groupIds.length === 0) return tree

  const updatedTree = structuredClone(tree)
  updatedTree.children = updatedTree.children
    .map((groupLevel) => {
      const isThatGroup = groupIds.includes(groupLevel.value?.id ?? "")
      if (isThatGroup) return groupLevel

      const hasThatGroup = groupIds.some((groupId) => doesGroupTreeHasGroup(groupLevel, groupId))
      if (hasThatGroup) {
        const updatedLevel = limitGroupTreeToGroups(groupLevel, groupIds)
        // Has that group, but itself is not a part of requested groups, so clear out it's nodes options
        updatedLevel.nodes = []
        return updatedLevel
      }

      return null
    })
    .filter((groupLevel) => groupLevel !== null)

  return updatedTree
}

const limitGroupTreeToNodes = (tree: GroupTreeNode, nodeIds: string[]) => {
  if (nodeIds.length === 0) return tree

  const updatedTree = structuredClone(tree)

  updatedTree.nodes = updatedTree.nodes.filter((nodeOption) =>
    nodeIds.includes(nodeOption.node.name),
  )

  updatedTree.children = updatedTree.children.map((treeLevel) =>
    limitGroupTreeToNodes(treeLevel, nodeIds),
  )

  return updatedTree
}

const limitGroupTreeBySearchText = (
  tree: GroupTreeNode,
  text: string,
  allNodeLabels?: string[],
) => {
  if (!text) return tree

  // Do not do that on all iterations, once on top level is enough
  const nodeLabels = allNodeLabels ? allNodeLabels : getNestedNodeLabels(tree)

  // If text is exact as one of the options, do not filter - show user all possible options
  // if (nodeLabels.includes(text)) return tree // no longer wanted behaviour

  const updatedTree = structuredClone(tree)

  updatedTree.nodes = updatedTree.nodes.filter((nodeOption) => {
    return nodeOption.display.label.toLocaleLowerCase().includes(text.toLowerCase())
  })

  updatedTree.children = updatedTree.children.map((treeLevel) =>
    limitGroupTreeBySearchText(treeLevel, text, nodeLabels),
  )

  return updatedTree
}

const createLabels = (
  graphData: GraphNodeEdgeData,
  value = "",
  groupFilter: string[] = [],
  optionsFilter: GroupNodeLabel[] = [],
): GroupTreeNode[] => {
  const limitToNodeIds = optionsFilter.map((option) => option.key)

  // Not the most performant approach to do it in steps, but it is fast enough even for huge graphs.
  // Thanks to that this complex logic is readable and each step has single responsibility which makes it maintantable.
  const root = createGroupTree(graphData.groupMap)
  const rootFilled = fillGroupTree(root, graphData.groupMap, graphData.nodeMap)
  const rootLimitedByGroups = limitGroupTreeToGroups(rootFilled, groupFilter)
  const rootLimitedByNodes = limitGroupTreeToNodes(rootLimitedByGroups, limitToNodeIds)
  const rootLimitedByText = limitGroupTreeBySearchText(rootLimitedByNodes, value)
  const rootWithoutLagNodes = filterOutLagNodeOptions(rootLimitedByText)
  const rootWithoutEmptyChildren = filterEmptyChildren(rootWithoutLagNodes)

  return rootWithoutEmptyChildren.children
}

const filterEmptyChildren = (tree: GroupTreeNode) => {
  const filterNode = (node: GroupTreeNode): GroupTreeNode | null => {
    const filteredChildren: GroupTreeNode[] = []

    node.children.forEach((child) => {
      const filteredChild = filterNode(child)
      if (filteredChild) filteredChildren.push(filteredChild)
    })

    if (node.nodes.length === 0 && filteredChildren.length === 0) return null

    const childrenUnchanged =
      filteredChildren.length === node.children.length &&
      filteredChildren.every((child, index) => child === node.children[index])

    if (childrenUnchanged) return node

    return { ...node, children: filteredChildren }
  }

  const filteredChildren: GroupTreeNode[] = []

  tree.children.forEach((child) => {
    const filteredChild = filterNode(child)
    if (filteredChild) filteredChildren.push(filteredChild)
  })

  const childrenUnchanged =
    filteredChildren.length === tree.children.length &&
    filteredChildren.every((child, index) => child === tree.children[index])

  if (childrenUnchanged) return tree

  return { ...tree, children: filteredChildren }
}

/**
 * We want to filter out lag nodes that have corresponding nodes.
 *
 * For example we should filter out "USA_gdp_per_capita_lag1" **IF** there is "USA_gdp_per_capita" node.
 */
const filterOutLagNodeOptions = (tree: GroupTreeNode) => {
  const allNodesLabels = getNestedNodeLabels(tree)

  const filterGroup = (groupOption: GroupTreeNode): GroupTreeNode => {
    const filteredChildren = groupOption.children.map(filterGroup)
    const filteredNodes = groupOption.nodes.filter((node) => {
      return !isLagNodeWithCorrespondingNode(node, allNodesLabels)
    })

    const nodesUnchanged = filteredNodes.length === groupOption.nodes.length
    const childrenUnchanged = filteredChildren.every(
      (child, index) => child === groupOption.children[index],
    )

    if (nodesUnchanged && childrenUnchanged) return groupOption

    return { ...groupOption, nodes: filteredNodes, children: filteredChildren }
  }

  const filteredChildren = tree.children.map(filterGroup)
  const childrenUnchanged = filteredChildren.every((child, index) => child === tree.children[index])

  if (childrenUnchanged) return tree

  return { ...tree, children: filteredChildren }
}

const getNestedNodeLabels = (tree: GroupTreeNode) => {
  return tree.children
    .reduce<GraphNodeData[]>((result, groupTree) => {
      return [...result, ...getNestedNodes(groupTree)]
    }, [])
    .map((node) => node.display.label)
}

const filterOutLagNodeOptionsFromGroupOption = (
  groupOption: GroupTreeNode,
  allNodesLabels: string[],
): GroupTreeNode => {
  const updatedGroupOption = structuredClone(groupOption)

  updatedGroupOption.nodes = updatedGroupOption.nodes.filter((node) => {
    return !isLagNodeWithCorrespondingNode(node, allNodesLabels)
  })

  updatedGroupOption.children = updatedGroupOption.children.map((option) =>
    filterOutLagNodeOptionsFromGroupOption(option, allNodesLabels),
  )

  return updatedGroupOption
}

const getNestedNodes = (groupTree: GroupTreeNode): GraphNodeData[] => {
  const nodes: GraphNodeData[] = [...groupTree.nodes]

  groupTree.children.forEach((chilrenGroupNode) => nodes.push(...getNestedNodes(chilrenGroupNode)))

  return nodes
}
